import { useEffect, useRef, useState } from "react";
import type { Pt } from "../lib/geometry";

export interface ViewBox {
	x: number;
	y: number;
	w: number;
	h: number;
}

interface Bounds {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
}

/** Края холста, закрытые панелями, пикселей */
export interface Insets {
	left?: number;
	right?: number;
	top?: number;
	bottom?: number;
}

const MIN_W = 500; // мм — максимальное приближение
const MAX_W = 200_000; // мм — максимальное отдаление

/**
 * Вьюпорт SVG в мировых координатах (мм).
 * Соотношение сторон viewBox всегда совпадает с размером контейнера,
 * поэтому перевод экран -> мир получается прямым.
 */
export const useViewport = (initial: ViewBox) => {
	const svgRef = useRef<SVGSVGElement | null>(null);
	const [box, setBox] = useState<ViewBox>(initial);

	// Держим h в соответствии с реальными пропорциями контейнера
	useEffect(() => {
		const el = svgRef.current;
		if (!el) return;
		const ro = new ResizeObserver(() => {
			const rect = el.getBoundingClientRect();
			if (rect.width < 1 || rect.height < 1) return;
			setBox((b) => {
				const h = b.w * (rect.height / rect.width);
				return Math.abs(h - b.h) < 0.5 ? b : { ...b, y: b.y + (b.h - h) / 2, h };
			});
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const mmPerPx = () => {
		const el = svgRef.current;
		if (!el) return 1;
		const rect = el.getBoundingClientRect();
		return rect.width > 0 ? box.w / rect.width : 1;
	};

	const toWorld = (clientX: number, clientY: number): Pt => {
		const el = svgRef.current;
		if (!el) return { x: 0, y: 0 };
		const rect = el.getBoundingClientRect();
		return {
			x: box.x + ((clientX - rect.left) / rect.width) * box.w,
			y: box.y + ((clientY - rect.top) / rect.height) * box.h,
		};
	};

	const zoomAt = (clientX: number, clientY: number, factor: number) => {
		const el = svgRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		const px = (clientX - rect.left) / rect.width;
		const py = (clientY - rect.top) / rect.height;
		setBox((b) => {
			const w = Math.min(MAX_W, Math.max(MIN_W, b.w * factor));
			const k = w / b.w;
			const h = b.h * k;
			return {
				w,
				h,
				x: b.x + (b.w - w) * px,
				y: b.y + (b.h - h) * py,
			};
		});
	};

	const panByPixels = (dxPx: number, dyPx: number) => {
		const el = svgRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		setBox((b) => ({
			...b,
			x: b.x - (dxPx / rect.width) * b.w,
			y: b.y - (dyPx / rect.height) * b.h,
		}));
	};

	/**
	 * Вписывает прямоугольник мира в видимую часть холста с отступом.
	 * `insets` — сколько пикселей по краям закрыто панелями, лежащими поверх холста:
	 * план центрируется в том, что реально видно, а не под панелями.
	 * Считаем на следующем кадре: на первом рендере SVG ещё может не иметь
	 * финальных размеров, и план встал бы не по центру.
	 */
	const fitNow = (bounds: Bounds, insets: Insets = {}) => {
		const el = svgRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		const left = insets.left ?? 0;
		const right = insets.right ?? 0;
		const top = insets.top ?? 0;
		const bottom = insets.bottom ?? 0;
		const visibleW = rect.width - left - right;
		const visibleH = rect.height - top - bottom;
		if (visibleW < 1 || visibleH < 1) return;
		const aspect = visibleH / visibleW;
		const pad = 1200;
		const bw = Math.max(2000, bounds.maxX - bounds.minX + pad * 2);
		const bh = Math.max(2000, bounds.maxY - bounds.minY + pad * 2);
		let w = bw;
		let h = w * aspect;
		if (h < bh) {
			h = bh;
			w = h / aspect;
		}
		// Миллиметров в пикселе — чтобы перевести отступы панелей в мир
		const scale = w / visibleW;
		const cx = (bounds.minX + bounds.maxX) / 2;
		const cy = (bounds.minY + bounds.maxY) / 2;
		setBox({
			x: cx - w / 2 - left * scale,
			y: cy - h / 2 - top * scale,
			w: rect.width * scale,
			h: rect.height * scale,
		});
	};

	const fit = (bounds: Bounds, insets?: Insets) => {
		requestAnimationFrame(() => fitNow(bounds, insets));
	};

	return { svgRef, box, setBox, toWorld, zoomAt, panByPixels, mmPerPx, fit };
};
